import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", email: "", role: "cashier", password: "", pin: "", active: true };
const ROLE_STYLE = { owner: "bg-black text-white", manager: "bg-blue-100 text-blue-700", cashier: "bg-secondary text-foreground", inventory: "bg-purple-100 text-purple-700", sales: "bg-teal-100 text-teal-700" };
const ALL_ROLES = ["owner", "manager", "cashier", "inventory", "sales"];

export default function Employees() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [form, setForm] = useState(null);

  const load = async () => {
    const [e, s] = await Promise.all([api.get("/employees"), api.get("/shifts")]);
    setEmployees(e.data); setShifts(s.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      if (form.id) {
        const upd = { name: form.name, role: form.role, active: form.active };
        if (form.password) upd.password = form.password;
        if (form.pin) upd.pin = form.pin;
        await api.put(`/employees/${form.id}`, upd);
      } else {
        await api.post("/employees", payload);
      }
      toast.success("Saved"); setForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const roles = ALL_ROLES;

  return (
    <div className="h-full overflow-y-auto p-6">
      <PageHeader title="Employees" subtitle="Roles, PINs & shifts">
        <button onClick={() => setForm({ ...EMPTY })} data-testid="add-employee-btn" className="h-12 px-5 rounded-xl bg-black text-white font-semibold flex items-center gap-2 active:scale-95"><Plus size={18} />Add employee</button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary border-b border-border text-left text-muted-foreground uppercase text-xs tracking-wider">
              <tr><th className="p-4">Name</th><th className="p-4 hidden sm:table-cell">Email</th><th className="p-4">Role</th><th className="p-4">2FA</th><th className="p-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} className="border-b border-border last:border-0">
                  <td className="p-4 font-semibold">{e.name}{!e.active && <span className="ml-2 text-xs text-destructive">(disabled)</span>}</td>
                  <td className="p-4 hidden sm:table-cell text-muted-foreground">{e.email}</td>
                  <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${ROLE_STYLE[e.role]}`}>{e.role}</span></td>
                  <td className="p-4">{e.two_factor_enabled ? <span className="text-emerald-600 font-medium">On</span> : <span className="text-muted-foreground">Off</span>}</td>
                  <td className="p-4">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setForm({ ...e, password: "", pin: "" })} data-testid={`emp-edit-${e.id}`} className="h-9 w-9 rounded-lg border border-border flex items-center justify-center active:scale-90"><Pencil size={16} /></button>
                      {e.role !== "owner" && <button onClick={() => del(e.id)} data-testid={`emp-del-${e.id}`} className="h-9 w-9 rounded-lg text-destructive border border-border flex items-center justify-center active:scale-90"><Trash2 size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><Clock size={20} />Recent shifts</h3>
          <div className="space-y-3 max-h-[420px] overflow-y-auto">
            {shifts.map((s) => (
              <div key={s.id} className="flex justify-between items-center text-sm border-b border-border pb-2">
                <span className="font-semibold">{s.user_name}</span>
                <span className="text-muted-foreground text-xs">{new Date(s.clock_in).toLocaleString()} {s.clock_out ? "→ out" : "· active"}</span>
              </div>
            ))}
            {shifts.length === 0 && <p className="text-muted-foreground text-sm">No shifts recorded.</p>}
          </div>
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md" data-testid="employee-modal">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="font-heading text-2xl font-bold tracking-tight">{form.id ? "Edit employee" : "Add employee"}</h2>
              <button onClick={() => setForm(null)} className="h-9 w-9 rounded-lg hover:bg-secondary flex items-center justify-center"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Name</label><input data-testid="ef-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp mt-1.5" /></div>
              {!form.id && <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Email</label><input data-testid="ef-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp mt-1.5" /></div>}
              <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Role</label>
                <select data-testid="ef-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="inp mt-1.5">
                  {roles.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Password</label><input data-testid="ef-password" type="password" placeholder={form.id ? "(unchanged)" : ""} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="inp mt-1.5" /></div>
                <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">PIN (4-6)</label><input data-testid="ef-pin" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="inp mt-1.5" /></div>
              </div>
              {form.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>}
              <button onClick={save} data-testid="ef-save" className="w-full h-13 rounded-xl bg-black text-white font-semibold active:scale-[0.98]">Save employee</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
